//
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

//
import { ExtraHeaderContent, Header } from 'adminDashboard/benefitDetails/common/ProductClassSummaryComponents'
import classNames from 'classnames'

import { ProductClassesInfo, updateOfferingRenewalClasses } from 'core'
import getProductClassDesignSummary, {
  ProductClassDesignSummaryResponse,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/getProductClassDesignSummary'
import { BenefitProductClassResponse } from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'

import {
  Button,
  Card,
  Col,
  Collapse,
  CollapseItem,
  Icon,
  Modal,
  Row,
  SimpleCheckbox,
  Skeleton,
  Switch,
  Tag,
  Tooltip,
} from 'common/components'
import { formatYear, parseZoneMonthYearFormat } from 'common/dateFormatters'
import { useBreakPoints, useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import QsehraBenefitDesignLogo from 'assets/images/Qsehra-benefit-design-icon 1.svg'

import SubCollapse from './SubCollapse'

//
import './product-class-details.less'

type ProductClassDetails2Props = {
  item: BenefitProductClassResponse
  collapseKey: number
  benefitStartDate: string
  benefitEndDate: string
  colSpan?: number
  showIcon?: boolean
  showBenefitDates?: boolean
  isRenewal?: boolean
  productClassesInfo: ProductClassesInfo
  onEdit?: (productClass: BenefitProductClassResponse) => void
  onDesignClass?: (productClass: BenefitProductClassResponse) => void
  onArchive?: (productClass: BenefitProductClassResponse) => void
  onDelete?: (productClass: BenefitProductClassResponse) => void
  onRenewalEdit?: (productClass: BenefitProductClassResponse) => void
  onRenewalDelete?: (productClass: BenefitProductClassResponse) => void
  onSubmit?: () => Promise<void>
}

const ProductClassDetailsV2 = ({
  item,
  collapseKey,
  benefitStartDate,
  benefitEndDate,
  colSpan = 14,
  showIcon,
  showBenefitDates,
  isRenewal,
  productClassesInfo,
  onEdit,
  onDesignClass,
  onArchive,
  onDelete,
  onRenewalDelete,
  onRenewalEdit,
  onSubmit,
}: ProductClassDetails2Props) => {
  const { t: translate } = useTranslation()
  const { isMd } = useBreakPoints()
  const [offer, setOffer] = useState(false)
  const [useSameAsPrevious, setUseSameAsPrevious] = useState(false)
  const { sendCommand: sendUpdateOfferingRenewalClasses } = useCommandRequestV2(
    ({ productClassId, currentOffer, currentSame }) =>
      updateOfferingRenewalClasses(productClassId, currentOffer, currentSame),
  )

  const { data: productClassDetails, isLoading: isProductClassDetailsLoading } =
    useQueryRequestV2<ProductClassDesignSummaryResponse>({
      queryFn: () => getProductClassDesignSummary(item.id),
    })
  const handleOfferChange = async (checked: boolean) => {
    const onChange = async () => {
      setOffer(checked)
      if (onSubmit) {
        await sendUpdateOfferingRenewalClasses({
          productClassId: item.id,
          currentOffer: checked,
          currentSame: useSameAsPrevious,
        })
        onSubmit()
      }
    }

    if (checked) {
      onChange()
    } else {
      Modal.confirm({
        title: `Are you sure you don't want to offer the “${item.name}” class for ${formatYear(benefitStartDate)}?`,
        content: (
          <div>
            <div className="mb-16">
              Removing this class from your 2024 benefit offering will not impact the current benefit year.
            </div>
            <div>
              Employees who are currently enrolled in this class will need to have their eligibility terminated or be
              assigned to another class.
            </div>
          </div>
        ),
        onOk() {
          onChange()
        },
        okText: 'Confirm',
        okType: 'primary',
        cancelText: 'Cancel',
      })
    }
  }

  const handleUseSameAsPreviousChange = async (e) => {
    const checked = e.target.checked
    setUseSameAsPrevious(checked)
    await sendUpdateOfferingRenewalClasses({ productClassId: item.id, currentOffer: offer, currentSame: checked })
  }

  useEffect(() => {
    if (productClassesInfo) {
      setOffer(productClassesInfo.offer)
      setUseSameAsPrevious(productClassesInfo.useSameAsPrevious)
    }
  }, [productClassesInfo])

  const editRenewalButton = (
    <div className="flex">
      <Button
        type="primary"
        ghost
        onClick={() => onRenewalEdit(item)}
        disabled={(!offer && productClassesInfo !== null) || useSameAsPrevious}
      >
        Edit settings
      </Button>
      {/* {productClassesInfo === null && (
        <Button
          onClick={() => {
            onRenewalDelete(item)
          }}
          danger
          className="ml-4"
          iconPosition="left"
        >
          <Icon className="fa fa-trash-alt" />
        </Button>
      )} */}
    </div>
  )

  return item.designCompleted || isRenewal ? (
    <Row className={'mb-8 t-product-class-details'}>
      <Col sm={24} lg={colSpan} className="pk-qsehra__design-classes">
        <Collapse className="design_classes_collapse" defaultActiveKey={[collapseKey]}>
          <CollapseItem
            className="class_design"
            key={collapseKey}
            header={
              <div className="flex">
                <Header title={item.name} />
                {isRenewal && productClassesInfo !== null && productClassesInfo?.isEdited && (
                  <Tag color="success" className="ml-8 flex flex__center color-grey-9">
                    Edited
                  </Tag>
                )}
              </div>
            }
            extra={
              <ExtraHeaderContent
                content={
                  <div>
                    {onEdit && (
                      <Button className="mr-16" onClick={() => onEdit(item)}>
                        {translate('admin-dashboard.benefit-details.summary.edit-button')}
                      </Button>
                    )}

                    {onDelete && (
                      <Icon onClick={() => onDelete(item)} className={'fal fa-trash-alt delete-class-btn'} />
                    )}

                    {onArchive && item.totalEmployees === 0 && <Button onClick={() => onArchive(item)}>Archive</Button>}

                    {isRenewal && productClassesInfo !== null && (
                      <div className="flex">
                        <div className="mr-8">Offering for {formatYear(benefitStartDate)}</div>
                        {/* <div className="mr-8">
                          <Icon className="far fa-info-circle color-cyan-6" />
                        </div> */}
                        <div>
                          <Switch checked={offer} onChange={handleOfferChange} />
                        </div>
                      </div>
                    )}
                    {isRenewal && productClassesInfo === null && editRenewalButton}
                  </div>
                }
              />
            }
            content={
              <Skeleton loading={isProductClassDetailsLoading}>
                <>
                  {isRenewal && productClassesInfo !== null && (
                    <div
                      className={classNames(
                        isMd ? 'flex__align_center' : '',
                        'w-100 flex mb-8 p-8 border-color-gray-4 rounded-4 py-16',
                        !productClassesInfo?.isNew ? 'flex_space_between' : 'flex__end',
                      )}
                    >
                      {!productClassesInfo?.isNew && (
                        <div className={classNames(isMd ? 'flex' : 'flex mb-4')}>
                          <div className="mr-8">
                            <SimpleCheckbox
                              checked={useSameAsPrevious}
                              onChange={(e) => {
                                const checked = e.target.checked
                                if (checked && productClassesInfo.isEdited) {
                                  Modal.confirm({
                                    title: `Are you sure you want to lose your edits to the “${
                                      item.name
                                    }” class for ${formatYear(benefitStartDate)}?`,
                                    content: (
                                      <div>
                                        <div className="mb-16">
                                          Choosing this option will remove any changes you made to this class for your
                                          2024 benefit offering and will not impact the current benefit year.
                                        </div>
                                        <div> If you want to make other changes you can re-select the Edit option.</div>
                                      </div>
                                    ),
                                    async onOk() {
                                      if (onSubmit) {
                                        await handleUseSameAsPreviousChange(e)
                                        onSubmit()
                                      }
                                    },
                                    okText: 'Confirm',
                                    okType: 'primary',
                                    cancelText: 'Cancel',
                                  })
                                } else handleUseSameAsPreviousChange(e)
                              }}
                            />
                          </div>
                          <div className="mr-8">Keep 2023 settings</div>
                          <div>
                            <Tooltip title="Unchecking this box for a class will allow you to edit settings for this class.">
                              <Icon className="far fa-info-circle color-cyan-6" />
                            </Tooltip>
                          </div>
                        </div>
                      )}
                      {editRenewalButton}
                    </div>
                  )}
                  {productClassDetails && (
                    <SubCollapse
                      productClassDetails={productClassDetails}
                      benefitStartDate={benefitStartDate}
                      benefitEndDate={benefitEndDate}
                    />
                  )}
                </>
              </Skeleton>
            }
          />
        </Collapse>
      </Col>
    </Row>
  ) : (
    <Row className={'mb-8'}>
      <Col sm={24} lg={17}>
        <Card>
          <Row align={'middle'} justify={'space-between'} className={'mb-16'}>
            <Col>
              <h5>{item.name}</h5>
            </Col>

            <Col>
              {onDelete && item.totalEmployees === 0 && (
                <Icon onClick={() => onDelete(item)} className={'fal fa-trash-alt delete-class-btn'} />
              )}
            </Col>
          </Row>

          <Row>
            <Col span={16}>
              {showBenefitDates && (
                <Row className={'mb-32 color-gray-7'}>
                  <Col span={18}>
                    <Icon className="fal fa-calendar-star mr-4 " />
                    {translate('admin-dashboard.benefit-details.qsehra-benefit-design.benefit-start-date')}
                    {parseZoneMonthYearFormat(benefitStartDate)}
                  </Col>
                </Row>
              )}

              {onDesignClass && (
                <Button type={'primary'} ghost onClick={() => onDesignClass(item)}>
                  {translate('admin-dashboard.benefit-details.qsehra-benefit-design.button-title')}
                </Button>
              )}
            </Col>

            {showIcon && (
              <Col span={8} className={'text-right'}>
                <img src={QsehraBenefitDesignLogo} alt={'logo'} />
              </Col>
            )}
          </Row>
        </Card>
      </Col>
    </Row>
  )
}

export default ProductClassDetailsV2
